import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Gauge, Wrench, AlertTriangle, CheckCircle2, Clock, History, User,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import SprayNav from '../../components/spray/SprayNav';
import Button from '../../components/ui/Button';
import AccessDenied from '../../components/ui/ModuleAccessDenied';
import CalibrationWizard from '../../components/spray/CalibrationWizard';
import CalibrationHistory from '../../components/spray/CalibrationHistory';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatDateInTz } from '../../utils/timezone';
import { toNum } from '@/utils/numeric';
import sprayService from '../../services/spray';
import equipmentService from '../../services/equipment';
import { isSprayer, type Equipment, type EquipmentTypeOption } from '../../types/equipment';
import type { EquipmentCalibration } from '../../types/spray';

function typeLabel(eq: Equipment, labels: Record<string, string>): string {
  if (eq.custom_type_label) return eq.custom_type_label;
  return labels[eq.equipment_type] || eq.equipment_type.replace(/_/g, ' ');
}

/**
 * A machine whose calibration could not be read.
 *
 * Distinct from `null`, which the API returns for a machine that genuinely has
 * never been calibrated — an ordinary state, not an error. Collapsing the two
 * would print "Never calibrated" over a machine that has been, which is a
 * statement this page has no grounds to make.
 */
const UNREADABLE = 'unreadable';
type Latest = EquipmentCalibration | null | typeof UNREADABLE;

type Standing = 'never' | 'unknown' | 'stale' | 'current';

function standingOf(cal: Latest): Standing {
  if (cal === UNREADABLE) return 'unknown';
  if (!cal) return 'never';
  return cal.is_stale === true ? 'stale' : 'current';
}

/**
 * Worst first. A machine nobody has ever measured is the most misleading thing
 * on the page — its stored rate is a number somebody typed — so it outranks one
 * whose standing merely could not be read, and both outrank an overdue machine.
 */
const STANDING_RANK: Record<Standing, number> = { never: 0, unknown: 1, stale: 2, current: 3 };

interface Row {
  equipment: Equipment;
  calibration: EquipmentCalibration | null;
  standing: Standing;
}

/**
 * Calibrate a sprayer on its own terms.
 *
 * Calibration already existed, but only ever hanging off a spray: the wizard
 * opened from a spray's detail page, or from the block a spray put in an
 * operator's way. That is the wrong time to find out a machine has never been
 * measured — the job is already planned and somebody is standing at the tank.
 *
 * This page is the same wizard with no spray attached. `spray_application_id`
 * is simply omitted, which the API has always allowed; a calibration recorded
 * here becomes the machine's current one exactly as one recorded mid-spray
 * does, and clears nothing, because there is nothing to clear.
 *
 * Machines come from the one equipment registry, narrowed by `isSprayer` —
 * the same predicate the equipment detail page uses to decide whether to show a
 * calibration card, and the same one `SprayEquipmentPicker` uses to decide what
 * a spray may be run with. A sprayer is a sub-type of equipment, not a separate
 * register, so there is nothing here to keep in step with anything else.
 */
function SprayCalibration() {
  const operator = useModuleLicense('spray', 'operator');
  const creator  = useModuleLicense('spray', 'creator');
  const timezone = useOrgTimezone();

  const [equipment, setEquipment]   = useState<Equipment[]>([]);
  const [typeLabels, setTypeLabels] = useState<Record<string, string>>({});
  const [latest, setLatest]         = useState<Record<number, Latest>>({});
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [showRetired, setShowRetired] = useState(false);

  const [calibrating, setCalibrating] = useState<Equipment | null>(null);
  const [openHistory, setOpenHistory] = useState<number | null>(null);
  const [historyKey, setHistoryKey]   = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [res, typesRes] = await Promise.all([
      equipmentService.list(),
      // Types are org-configurable, so the label for a custom sprayer type only
      // exists here - a hardcoded map would print the raw key for those.
      equipmentService.getTypes().catch(() => ({ success: false, data: [] as EquipmentTypeOption[] })),
    ]);
    if (!res.success || !res.data) {
      setEquipment([]);
      setError('Could not load the equipment registry.');
      setLoading(false);
      return;
    }
    if (typesRes.success && typesRes.data) {
      setTypeLabels(Object.fromEntries(typesRes.data.map(t => [t.type_key, t.label])));
    }
    // Only a sprayer has an output rate to measure. Everything else in the
    // registry - tractors, bins, slashers - is noise on this page.
    const list = res.data.filter(eq => isSprayer(eq));
    setEquipment(list);

    // One request per machine, all in flight together. A machine whose latest
    // call fails is marked unreadable rather than failing the page — the other
    // machines' standing is still worth reading.
    const results = await Promise.all(
      list.map(eq =>
        sprayService.getLatestCalibration(eq.id)
          .then(r => [eq.id, r.success ? (r.data ?? null) : UNREADABLE] as Readonly<[number, Latest]>)
          .catch(() => [eq.id, UNREADABLE] as Readonly<[number, Latest]>)
      )
    );
    setLatest(Object.fromEntries(results));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows: Row[] = useMemo(() => {
    return equipment
      .filter(eq => showRetired || eq.status !== 'retired')
      .map(eq => {
        const read = latest[eq.id] ?? null;
        return {
          equipment: eq,
          calibration: read === UNREADABLE ? null : read,
          standing: standingOf(read),
        };
      })
      .sort((a, b) => {
        const byStanding = STANDING_RANK[a.standing] - STANDING_RANK[b.standing];
        if (byStanding !== 0) return byStanding;
        // Within a standing, oldest measurement first — the next one due.
        const ageA = a.calibration?.age_days ?? Number.POSITIVE_INFINITY;
        const ageB = b.calibration?.age_days ?? Number.POSITIVE_INFINITY;
        if (ageA !== ageB) return ageB - ageA;
        return a.equipment.name.localeCompare(b.equipment.name);
      });
  }, [equipment, latest, showRetired]);

  const counts = useMemo(() => ({
    never:   rows.filter(r => r.standing === 'never').length,
    stale:   rows.filter(r => r.standing === 'stale').length,
    current: rows.filter(r => r.standing === 'current').length,
  }), [rows]);

  const onSaved = (calibration: EquipmentCalibration) => {
    setLatest(prev => ({ ...prev, [calibration.equipment_id]: calibration }));
    setHistoryKey(k => k + 1);
    setCalibrating(null);
  };

  const hasAccess = operator.hasAccess || creator.hasAccess;
  if (operator.loading && creator.loading) return null;
  if (!hasAccess) return <AccessDenied moduleName="Spray" />;

  return (
    <>
      <PageHeader
        title="Calibration"
        subtitle="Measure what a sprayer actually puts out — no spray required"
        icon={<Gauge className="h-6 w-6 text-white" />}
      />
      <SprayNav />

      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <p className="text-sm text-neutral-600">
          A calibration recorded here becomes the machine&rsquo;s current one, the same as one
          recorded during a spray. Do it after changing nozzles, servicing a pump, or whenever
          the figure below has gone stale — rather than when a job is already waiting on it.
        </p>

        {/* Standing at a glance */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-neutral-300 bg-neutral-50 p-3">
            <p className="text-2xl font-semibold text-neutral-900">{counts.never}</p>
            <p className="text-xs text-neutral-600 mt-0.5">Never calibrated</p>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-2xl font-semibold text-amber-800">{counts.stale}</p>
            <p className="text-xs text-amber-700 mt-0.5">Overdue</p>
          </div>
          <div className="rounded-lg border border-green-200 bg-green-50 p-3">
            <p className="text-2xl font-semibold text-green-800">{counts.current}</p>
            <p className="text-xs text-green-700 mt-0.5">Current</p>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
          <input
            type="checkbox"
            checked={showRetired}
            onChange={e => setShowRetired(e.target.checked)}
          />
          Include retired equipment
        </label>

        {error && (
          <p className="text-sm text-red-600 flex items-start gap-1.5">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            {error}
          </p>
        )}

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-28 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16">
            <Wrench className="h-12 w-12 mx-auto mb-3 text-neutral-300" />
            <p className="font-medium text-neutral-600">No sprayers on the equipment registry</p>
            <p className="text-sm text-neutral-400 mt-1">
              Add a machine under Equipment with a sprayer type — airblast, boom, spray tank,
              knapsack or drone — and it will appear here to calibrate.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map(({ equipment: eq, calibration, standing }) => (
              <div
                key={eq.id}
                className={`rounded-xl border p-4 ${
                  standing === 'never' || standing === 'unknown' ? 'border-neutral-300 bg-neutral-50'
                  : standing === 'stale' ? 'border-amber-200 bg-amber-50/60'
                  : 'border-neutral-200 bg-white'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-neutral-900 break-words">{eq.name}</p>
                    <p className="text-xs text-neutral-500 break-words">
                      {[typeLabel(eq, typeLabels), eq.make, eq.model].filter(Boolean).join(' · ')}
                      {eq.status !== 'active' && ` · ${eq.status.replace('_', ' ')}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => setCalibrating(eq)}>
                      <Wrench className="h-4 w-4" />
                      {calibration ? 'Recalibrate' : 'Calibrate'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setOpenHistory(prev => (prev === eq.id ? null : eq.id))}
                      aria-expanded={openHistory === eq.id}
                    >
                      <History className="h-4 w-4" />
                      History
                    </Button>
                  </div>
                </div>

                {/* What it was last measured at */}
                {calibration ? (
                  <div className="mt-3 space-y-1">
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                      <span className="text-lg font-semibold text-neutral-900">
                        {toNum(calibration.output_rate_lpha) ?? '—'} L/ha
                        {calibration.swath_source === 'strip' && (
                          <span className="text-sm font-normal text-neutral-500">
                            {' '}sprayed, {toNum(calibration.reference_swath_m)} m strip
                          </span>
                        )}
                      </span>
                      <span className="text-sm text-neutral-600">
                        {toNum(calibration.total_flow_lpm)} L/min at {toNum(calibration.speed_kph)} km/h
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDateInTz(calibration.calibrated_at, timezone)}
                        {calibration.age_days != null && ` · ${calibration.age_days} days ago`}
                      </span>
                      {(calibration.calibrated_by_first_name || calibration.calibrated_by_last_name) && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {calibration.calibrated_by_first_name} {calibration.calibrated_by_last_name}
                        </span>
                      )}
                      {standing === 'current' && (
                        <span className="flex items-center gap-1 text-green-700">
                          <CheckCircle2 className="h-3 w-3" />
                          Current
                        </span>
                      )}
                    </div>
                    {standing === 'stale' && (
                      <p className="text-xs text-amber-700 flex items-start gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        Older than your organisation&rsquo;s {calibration.valid_days}-day limit.
                      </p>
                    )}
                  </div>
                ) : standing === 'unknown' ? (
                  <p className="mt-3 text-sm text-neutral-600 flex items-start gap-1.5">
                    <AlertTriangle className="h-4 w-4 text-neutral-400 shrink-0 mt-0.5" />
                    Could not read this machine&rsquo;s calibration. It may still have one —
                    reload before assuming otherwise.
                  </p>
                ) : (
                  <p className="mt-3 text-sm text-neutral-600">
                    <strong>Never calibrated.</strong> Any water volume or tank count for this
                    sprayer is an estimate from figures someone typed, not from anything the
                    machine has been measured doing.
                  </p>
                )}

                {openHistory === eq.id && (
                  <div className="mt-4">
                    <CalibrationHistory
                      equipmentId={eq.id}
                      refreshKey={historyKey}
                      onCalibrate={() => setCalibrating(eq)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* No sprayApplicationId: this calibration answers to no spray. */}
      {calibrating && (
        <CalibrationWizard
          equipment={calibrating}
          onClose={() => setCalibrating(null)}
          onSaved={onSaved}
        />
      )}
    </>
  );
}

export default SprayCalibration;
